import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { IAccount, IAccountDetail, IGmfParams, IGmfPJParams } from '@app/shared/models/banking.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, EMPTY, of } from 'rxjs';
import { takeUntil, first, switchMap, tap, catchError, filter } from 'rxjs/operators';

@Component({
  selector: 'app-exoneration-pj',
  templateUrl: './exoneration-pj.component.html',
  styleUrls: ['./exoneration-pj.component.sass'],
})
export class ExonerationPJComponent extends SubscriberComponent implements OnInit, OnDestroy {
  account!: IAccountDetail | IAccount;
  initialState!: IDynamicDialogConfig;
  private closeModal$ = new Subject<void>();

  // Form for exoneration type selection
  exonerationForm = new FormGroup({
    gmfReason: new FormControl('', [Validators.required])
  });

  // GMF reason options
  gmfReasonTypes = [
    { value: 'TIPO1', label: 'UVT' },
    { value: 'TIPO2', label: 'Pasarela de pago' },
    { value: 'TIPO3', label: 'Beneficio impositivo' }
  ];

  // Currently selected exoneration type
  selectedExonerationType = '';

  // QA SIMULATION: UI state management - Remove when real endpoint is ready
  isExonerationMode = true; // true = select exoneration, false = show desmarcar
  currentExonerationType = '';
  buttonText = 'MODAL_EXONERATION_PJ.APPLY_BUTTON'; // Dynamic button text

  constructor(
    private readonly modalSvc: ModalService,
    private readonly _accountBasicSvc: AccountBasicService,
    private readonly _accountCtx: AccountContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.getmodalData();
    this.logAmplitudeEvent();
    this.setupModalCloseSubscription();
    this.setupButtonValidation(); // Professional reactive solution

    const accountCode = this._accountCtx.getAccountCode() || '';
    const getAccounts = this._accountCtx.getAccounts() || [];
    this.account = getAccounts.find((acc: any) => acc.code === accountCode) || ({} as IAccountDetail);

    // QA SIMULATION: Detect existing exoneration state
    this.detectExistingExoneration();

    // Subscribe to account info changes for reactive updates
    this.subscribeToAccountChanges();

    this.confirmActionSubscription(accountCode);
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this.modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this.modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  /**
   * Creates the payload for GMF PJ exoneration request
   * QA SIMULATION: Handles both exoneration and unmark operations
   */
  private createPayload(accountCode: string, agentData: any): IGmfPJParams {
    if (this.isExonerationMode) {
      // EXONERATION MODE: Apply new exoneration
      return {
        rppUserId: this.accountInfo?.user_code || '',
        accountCode,
        isGmf: true, // Set to true to exonerate
        agent_email: agentData?.email || '',
        gmfReason: this.selectedExonerationType
      };
    } else {
      // UNMARK MODE: Remove existing exoneration
      return {
        rppUserId: this.accountInfo?.user_code || '',
        accountCode,
        isGmf: false, // Set to false to remove exoneration
        agent_email: agentData?.email || '',
        gmfReason: this.currentExonerationType // Use current type for tracking
      };
    }
  }

  /**
   * Handles the confirmation action when primary button is clicked
   * QA SIMULATION: Supports both exoneration and unmark operations
   */
  confirmActionSubscription(accountCode: string) {
    return this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        first(value => value), // Only take the first true value
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => {
          const payload: IGmfPJParams = this.createPayload(accountCode, agentData);
          console.log(' SENDING PJ GMF PAYLOAD:', payload);

          return this._accountBasicSvc.setGmfPJ(payload).pipe(
            catchError(error => {
              console.error('❌ PJ GMF SERVICE ERROR:', error);
              this.modalSvc.close();

              return EMPTY;
            })
          );
        }),
        tap(() => {
          const operation = this.isExonerationMode ? 'EXONERATION' : 'UNMARK';
          console.log(` PJ GMF ${operation} COMPLETED`);
          this.modalSvc.close();
        })
      )
      .subscribe();
  }

  private logAmplitudeEvent() {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.EXONERATION,
    });
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => (this.initialState = data),
    });
  }

  get accountInfo(): any {
    return this._accountBasicSvc.accountInfo;
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.unsubscribe();
  }

  /**
   * QA-FRIENDLY: Handles checkbox change events for both exoneration and unmark modes
   */
  onCheckboxChange(event: any, typeValue: string): void {
    if (!this.isExonerationMode) {
      // DESMARCAR MODE: Can only uncheck the current type to enable "Desmarcar" button
      if (typeValue === this.currentExonerationType) {
        this.selectedExonerationType = event.checked ? typeValue : '';
        this.exonerationForm.get('gmfReason')?.setValue(this.selectedExonerationType);

        console.log('🔄 DESMARCAR MODE:', {
          action: event.checked ? 'RE-CHECKED' : 'UNCHECKED',
          currentType: this.currentExonerationType,
          canDesmarcar: !event.checked
        });
      }
    } else {
      // EXONERAR MODE: Radio button behavior for selection
      if (event.checked) {
        this.selectedExonerationType = typeValue;
        this.exonerationForm.get('gmfReason')?.setValue(typeValue);
      } else {
        this.selectedExonerationType = '';
        this.exonerationForm.get('gmfReason')?.setValue('');
      }

      console.log('🔄 EXONERAR MODE:', {
        action: event.checked ? 'SELECTED' : 'UNSELECTED',
        selectedType: this.selectedExonerationType
      });
    }

    // Update button state based on new selection
    this.updateButtonState();
  }

  /**
   * Legacy method for backward compatibility (kept for any external references)
   * @param typeValue The value of the selected type
   */
  selectExonerationType(typeValue: string): void {
    // This method is now primarily handled by onCheckboxChange
    // Kept for compatibility but simplified
    if (this.selectedExonerationType === typeValue) {
      this.selectedExonerationType = '';
      this.exonerationForm.get('gmfReason')?.setValue('');
    } else {
      this.selectedExonerationType = typeValue;
      this.exonerationForm.get('gmfReason')?.setValue(typeValue);
    }
  }

  /**
   * Sets up professional reactive button validation
   */
  private setupButtonValidation(): void {
    // Watch form changes reactively
    this.exonerationForm.valueChanges.subscribe(() => {
      this.updateButtonStateReactive();
    });

    // Set initial state immediately
    this.updateButtonStateReactive();
  }

  /**
   * Professional reactive button state management
   */
  private updateButtonStateReactive(): void {
    if (this.initialState?.modalComponentData) {
      const isDisabled = !this.isFormValid;
      this.initialState.modalComponentData.acceptButtonTitleDisabled = isDisabled;

      // Reactive update without setTimeout
      this.modalSvc.setModalData(this.initialState);
    }
  }

  /**
   * QA-FRIENDLY: Determines if a checkbox should be disabled
   * For exonerated accounts: disable all except current type
   * For unexonerated accounts: use radio button behavior (disable others when one selected)
   */
  isCheckboxDisabled(gmfReasonType: string): boolean {
    if (!this.isExonerationMode) {
      // DESMARCAR MODE: All checkboxes except current type are disabled
      return gmfReasonType !== this.currentExonerationType;
    } else {
      // EXONERAR MODE: Radio button behavior - disable others when one is selected
      return this.selectedExonerationType !== '' && this.selectedExonerationType !== gmfReasonType;
    }
  }

  /**
   * QA-FRIENDLY: Form validation for simplified design
   * Exonerar mode: need selection
   * Desmarcar mode: need to uncheck current selection
   */
  get isFormValid(): boolean {
    if (!this.isExonerationMode) {
      // DESMARCAR MODE: Valid when current type is unchecked (selectedExonerationType is empty)
      return this.selectedExonerationType === '';
    } else {
      // EXONERAR MODE: Valid when something is selected
      return this.selectedExonerationType !== '' && this.selectedExonerationType.length > 0;
    }
  }

  /**
   * QA-FRIENDLY: Returns appropriate warning message based on current mode
   * Remove when real endpoint is ready
   */
  getWarningMessage(): string {
    if (!this.isExonerationMode) {
      // DESMARCAR MODE: Warning about removing exoneration
      return 'MODAL_EXONERATION_PJ.ALERT_UNMARK';
    } else {
      // EXONERAR MODE: Warning about applying exoneration
      return 'MODAL_EXONERATION.ALERT';
    }
  }

  /**
   * QA-FRIENDLY: Detects existing PJ exoneration state and sets up checkbox states
   * Remove when real endpoint is ready
   */
  private detectExistingExoneration(): void {
    const accountInfo = this.accountInfo;
    const isAlreadyExonerated = accountInfo?.is_gmf && accountInfo.tax?.values?.[0]?.gmf_reason;

    if (isAlreadyExonerated) {
      // DESMARCAR MODE: Pre-select current type, disable others, button disabled until unchecked
      this.isExonerationMode = false;
      this.currentExonerationType = accountInfo.tax?.values?.[0]?.gmf_reason!;
      this.selectedExonerationType = accountInfo.tax?.values?.[0]?.gmf_reason!; // Pre-select current type
      this.buttonText = 'Desmarcar';
    } else {
      // EXONERAR MODE: No pre-selection, all available, button disabled until selection
      this.isExonerationMode = true;
      this.currentExonerationType = '';
      this.selectedExonerationType = ''; // No pre-selection
      this.buttonText = 'MODAL_EXONERATION_PJ.APPLY_BUTTON';
    }

    // Update modal button text and state
    this.updateModalButtonText();
    this.updateButtonState();
  }

  /**
   * QA-FRIENDLY: Updates modal button text based on current mode
   * Remove when real endpoint is ready
   */
  private updateModalButtonText(): void {
    if (this.initialState?.modalComponentData) {
      // Update button text in modal configuration
      this.initialState.acceptButtonTitle = this.buttonText;
      this.modalSvc.setModalData(this.initialState);
    }
  }

  /**
   * QA-FRIENDLY: Updates modal button state based on form validation
   * Remove when real endpoint is ready
   */
  private updateButtonState(): void {
    if (this.initialState?.modalComponentData) {
      const isValid = this.isFormValid;

      // Update button state in modal configuration using the correct property
      const updatedState = {
        ...this.initialState,
        acceptButtonDisabled: !isValid
      };
      this.modalSvc.setModalData(updatedState);

      console.log('🔘 BUTTON STATE UPDATED:', {
        mode: this.isExonerationMode ? 'EXONERAR' : 'DESMARCAR',
        selectedType: this.selectedExonerationType,
        isValid,
        buttonEnabled: isValid
      });
    }
  }

  /**
   * QA SIMULATION: Subscribes to account info changes for reactive UI updates
   * Remove when real endpoint is ready
   */
  private subscribeToAccountChanges(): void {
    this._accountBasicSvc.accountInfo$
      .pipe(
        takeUntil(this.closeModal$),
        filter(accountInfo => !!accountInfo && !!accountInfo.code)
      )
      .subscribe(accountInfo => {
        console.log('🔄 ACCOUNT INFO CHANGED:', {
          is_gmf: accountInfo.is_gmf,
          gmf_reason: accountInfo.tax.values[0].gmf_reason
        });

        // Update local account reference
        this.account = accountInfo;

        // Re-detect exoneration state when account info changes
        this.detectExistingExoneration();
      });
  }
}
